"use client";

import React from "react";
import { ChevronLeft, ChevronRight, MessageCircle, RefreshCw } from "lucide-react";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  ProductSortDropdown,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";

function ProductGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="h-[400px] w-full animate-pulse rounded-lg bg-muted"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="mb-4 text-muted-foreground">
          Si è verificato un errore nel caricamento dei prodotti.
        </p>
        <Button
          onClick={() => refetch()}
          variant="outline"
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Riprova
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-lg text-muted-foreground">
          Nessun prodotto trovato.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => (
          <div
            key={product.id}
            data-index={i}
            className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-all hover:shadow-md"
          >
            <Link
              to={product.link}
              className="relative aspect-square overflow-hidden bg-muted"
            >
              {product.featured_image_url ? (
                <img
                  src={product.featured_image_url}
                  alt={product.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-secondary text-secondary-foreground">
                  <span className="text-sm font-medium">No Immagine</span>
                </div>
              )}
            </Link>

            <div className="flex flex-1 flex-col p-6">
              <Link to={product.link} className="mb-2 block">
                <h3 className="font-serif text-xl font-medium text-card-foreground transition-colors hover:text-primary">
                  {product.title}
                </h3>
              </Link>

              {product.short_description && (
                <div
                  className="mb-6 line-clamp-2 text-sm text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html: product.short_description,
                  }}
                />
              )}

              <div className="mt-auto">
                <Button
                  asChild
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Link
                    to={`https://wa.me/?text=${encodeURIComponent(
                      `Ciao! Vorrei informazioni sulla torta: ${product.title}`
                    )}`}
                    newTab
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Richiedi informazioni
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-12">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (hasPrev) prevPage();
                  }}
                  className={`gap-1 px-2.5 sm:pl-2.5 ${
                    !hasPrev ? "pointer-events-none opacity-50" : ""
                  }`}
                  size="default"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:block">Precedente</span>
                </PaginationLink>
              </PaginationItem>

              <PaginationItem>
                <span className="flex h-9 items-center px-4 text-sm font-medium text-muted-foreground">
                  Pagina {currentPage} di {totalPages}
                </span>
              </PaginationItem>

              <PaginationItem>
                <PaginationLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (hasNext) nextPage();
                  }}
                  className={`gap-1 px-2.5 sm:pr-2.5 ${
                    !hasNext ? "pointer-events-none opacity-50" : ""
                  }`}
                  size="default"
                >
                  <span className="hidden sm:block">Successivo</span>
                  <ChevronRight className="h-4 w-4" />
                </PaginationLink>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </>
  );
}

export default function ProductGridAndPagination() {
  const current_wp_query_params =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section
      id="product-grid-and-pagination"
      className="bg-background py-16 md:py-24"
    >
      <div className="container mx-auto px-4">
        <EcommerceProductsProvider wp_query={wp_query} className="flex flex-col gap-8">
          <div className="flex flex-col items-start justify-between gap-4 border-b border-border pb-6 sm:flex-row sm:items-center">
            <h2 className="font-serif text-3xl font-bold text-foreground md:text-4xl">
              Le nostre torte su ordinazione
            </h2>
            <ProductSortDropdown label="Ordina per" />
          </div>

          <ProductGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}